import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";

const AMIrrigationPlannerSummary = ({
  irrigationDates,
  totalIrrigationConverted,
  totalRainfallConverted,
  totalIrrigationNeeded,
  totalRainfall,
}) => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        p: 3,
        mb: 3,
        border: "1px solid #e3f2fd",
        borderRadius: 2,
        bgcolor: "#f3f9ff",
      }}
    >
      <Typography
        variant="h6"
        sx={{ color: "#1976d2", fontWeight: "bold", mb: 1 }}
      >
        {t("Your 7-Day Watering Plan")}
      </Typography>

      <Typography
        variant="body2"
        sx={{ color: "#666", mb: 3, fontStyle: "italic" }}
      >
        {t("Here's how much water your crops need this week")}
      </Typography>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mb: 3 }}>
        <Box sx={{ minWidth: "200px" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            💧 {t("Water You Need to Give")}
          </Typography>
          <Typography
            variant="h6"
            sx={{ color: "#1976d2", fontWeight: "bold" }}
          >
            {totalIrrigationConverted.liters} {t("liters per acre")}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block" }}
          >
            {totalIrrigationConverted.description}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: "0.7rem", opacity: 0.8 }}
          >
            ({totalIrrigationNeeded.toFixed(1)} mm)
          </Typography>
        </Box>

        <Box sx={{ minWidth: "200px" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            🌧️ {t("Rain Coming This Week")}
          </Typography>
          <Typography
            variant="h6"
            sx={{ color: "#388e3c", fontWeight: "bold" }}
          >
            {totalRainfallConverted.liters} {t("liters per acre")}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block" }}
          >
            {totalRainfallConverted.description}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: "0.7rem", opacity: 0.8 }}
          >
            ({totalRainfall.toFixed(1)} mm)
          </Typography>
        </Box>

        <Box sx={{ minWidth: "180px" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            📅 {t("Days to Water")}
          </Typography>
          <Typography
            variant="h6"
            sx={{ color: "#f57c00", fontWeight: "bold" }}
          >
            {irrigationDates.length}{" "}
            {irrigationDates.length === 1 ? t("day") : t("days")}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {irrigationDates.length === 0
              ? t("Rest week for you!")
              : irrigationDates.length <= 2
                ? t("Easy week ahead")
                : irrigationDates.length <= 4
                  ? t("Moderate work needed")
                  : t("Busy watering week")}
          </Typography>
        </Box>
      </Box>

      {irrigationDates.length > 0 ? (
        <Box>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", mb: 2, color: "#1976d2" }}
          >
            📋 {t("When to Water Your Crops")}:
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {irrigationDates.map((irrigation, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 2.5,
                  bgcolor: "white",
                  borderRadius: 2,
                  border: "2px solid #e3f2fd",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                  "&:hover": {
                    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                    transform: "translateY(-1px)",
                    transition: "all 0.2s ease",
                  },
                }}
              >
                <Box>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: "medium", mb: 0.5 }}
                  >
                    {irrigation.formattedDate}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {index === 0 && t("Start with this day")}
                    {index === irrigationDates.length - 1 &&
                      index > 0 &&
                      t("Final watering")}
                    {index > 0 &&
                      index < irrigationDates.length - 1 &&
                      t("Continue watering")}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography
                    variant="h6"
                    sx={{ color: "#1976d2", fontWeight: "bold" }}
                  >
                    {irrigation.converted.liters} {t("L/acre")}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block" }}
                  >
                    {irrigation.converted.description}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.7rem", opacity: 0.7 }}
                  >
                    ({irrigation.amount.toFixed(1)} mm)
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              mt: 2,
              p: 2,
              bgcolor: "#f0f7ff",
              borderRadius: 1,
              border: "1px solid #b3d9ff",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "#1565c0", fontWeight: "medium", mb: 1 }}
            >
              💡 {t("Pro Tips")}:
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#1565c0", fontSize: "0.85rem", lineHeight: 1.4 }}
            >
              •{" "}
              {t(
                "Water early morning (6-8 AM) or evening (6-8 PM) for best results"
              )}
              <br />•{" "}
              {t(
                "Liters per acre = total water needed for your entire acre of land"
              )}
              <br />•{" "}
              {t(
                "Check soil moisture by inserting finger 2-3 inches deep before watering"
              )}
              <br />•{" "}
              {t("Water slowly and deeply rather than quick surface watering")}
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            p: 3,
            bgcolor: "#e8f5e8",
            borderRadius: 2,
            border: "2px solid #c8e6c9",
            textAlign: "center",
          }}
        >
          <Typography
            variant="h6"
            sx={{ color: "#2e7d32", fontWeight: "bold", mb: 1 }}
          >
            🌧️ {t("Great News!")}
          </Typography>
          <Typography variant="body1" sx={{ color: "#2e7d32", mb: 1 }}>
            {t("No watering needed this week - Mother Nature has you covered!")}
          </Typography>
          <Typography variant="body2" sx={{ color: "#388e3c" }}>
            {t(
              "The expected rainfall will provide enough water for your crops. You can relax this week!"
            )}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AMIrrigationPlannerSummary;
